import moment from 'moment';
import {
  AppPushNotificationConfiguration,
  PushNotification,
  PushNotificationOptions,
  PushNotificationTarget,
  SchedulePushdNotificationBody,
  SendPushdNotificationBody,
} from '../../types/notification';
import {
  SegmentDB,
  Segment,
  CreateSegmentFromPollBody,
  CreateSegmentFromPollResponse,
} from '../../types/segment';
import { Optional } from 'utility-types';

type ScheduledNotificationPayload = Omit<SchedulePushdNotificationBody, 'status'>;

const API = process.env.REACT_APP_PUSH_NOTIFICATIONS;

const API_SEGMENTS = `${API}/segments`;
const API_SEND_NOTIFICATION = `${API}/notifications/send`;
const API_CONFIGURATION = `${API}/configurations`;

const getScheduleApiUrl = (path: string) => {
  if (!API) {
    throw new Error('Missing REACT_APP_PUSH_NOTIFICATIONS environment variable');
  }

  return `${API}/schedule${path}`;
};

const getHeaders = (auth?: string, extraHeaders?: HeadersInit): HeadersInit => ({
  ...(extraHeaders || {}),
  ...(auth
    ? {
        Authorization: `Bearer ${auth}`,
      }
    : {}),
});

export const getPushNotificationConfiguration = async (abbrv: string, auth?: string) => {
  const response = await fetch(`${API_CONFIGURATION}/${abbrv}`, {
    method: 'GET',
    headers: getHeaders(auth),
  });

  const appPushNotificationConfiguration: AppPushNotificationConfiguration = await response.json();

  return appPushNotificationConfiguration;
};

export const createPushNotificationConfiguration = async (
  body: AppPushNotificationConfiguration,
  auth?: string
): Promise<{
  message?: string;
  id?: string;
  error?: string;
  statusCode?: string;
}> => {
  const response = await fetch(`${API_CONFIGURATION}/${body.app}`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: getHeaders(auth, {
      'Content-Type': 'application/json',
    }),
  });

  const output = await response.json();
  return output;
};

export const updatePushNotificationConfiguration = async (
  body: AppPushNotificationConfiguration,
  auth?: string
): Promise<{
  message?: string;
  id?: string;
  error?: string;
  statusCode?: string;
}> => {
  const response = await fetch(`${API_CONFIGURATION}/${body.app}`, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: getHeaders(auth, {
      'Content-Type': 'application/json',
    }),
  });

  const output = await response.json();
  return output;
};

export const getSegments = async (abbrv: string, auth?: string): Promise<Segment[]> => {
  const response = await fetch(`${API_SEGMENTS}/list/${abbrv}`, {
    method: 'GET',
    headers: getHeaders(auth),
  });
  const {
    segments,
  }: {
    segments: SegmentDB[];
  } = await response.json();
  return segments.map((item) => {
    const id: string = (() => {
      if (item.type === 'SEGMENT') {
        return item.segmentId as string;
      }
      if (item.type === 'TOPIC') {
        return item.id as string;
      }

      return '';
    })();

    return {
      ...item,
      id,
    };
  });
};

export const createSegment = async (
  abbrv: string,
  body: FormData,
  auth?: string
): Promise<{ id: string; message: string }> => {
  const res = await fetch(`${API_SEGMENTS}/create/${abbrv}`, {
    method: 'POST',
    body: body,
    headers: getHeaders(auth),
  });

  const response = await res.json();
  return response;
};

export const createSegmentFromPoll = async (
  abbrv: string,
  body: CreateSegmentFromPollBody,
  auth?: string
): Promise<CreateSegmentFromPollResponse> => {
  const res = await fetch(`${API_SEGMENTS}/create-from-poll/${abbrv}`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: getHeaders(auth, { 'Content-Type': 'application/json' }),
  });

  const response = await res.json();
  if (!res.ok) {
    throw new Error(response?.message || response?.error || `Request failed (${res.status})`);
  }
  return response;
};

export const updateSegment = async (
  id: string,
  body: FormData,
  auth?: string
): Promise<{ id: string; message: string }> => {
  // Encoded, since older segment ids can contain characters like "?" that would cut the URL
  const res = await fetch(`${API_SEGMENTS}/update/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: body,
    headers: getHeaders(auth),
  });

  const response = await res.json();
  return response;
};

export const deleteSegment = async (
  id: string,
  auth?: string
): Promise<{ id: string; message: string }> => {
  // Encoded, since older segment ids can contain characters like "?" that would cut the URL
  const res = await fetch(`${API_SEGMENTS}/delete/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: getHeaders(auth),
  });

  const response = await res.json();
  if (response.error) {
    throw new Error(response?.error || '');
  }
  return response;
};

export const sendNotification = async (
  abbrv: string,
  body: SendPushdNotificationBody,
  collapseKey?: string,
  auth?: string
): Promise<{ id: string; message: string }> => {
  const res = await fetch(`${API_SEND_NOTIFICATION}/${abbrv}`, {
    method: 'POST',
    headers: getHeaders(auth, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({
      ...body,
      notificationPriority: 'PRIORITY_MAX',
      apnsPriority: '10',
      apnsPushType: 'alert',
      analyticsLabel: 'GENERIC',
      apsSound: 'default',
      apsMutableContent: 1,
      androidPriority: 'high',
      collapseKey,
    } satisfies PushNotification & PushNotificationOptions),
  });

  const response = await res.json();
  if (response.error) {
    throw new Error(response?.error || '');
  }

  return response;
};

export const scheduleNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: Optional<ScheduledNotificationPayload, 'id' | 'createdAt'>;
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const res = await fetch(getScheduleApiUrl(`/${abbrv}`), {
    method: 'POST',
    body: JSON.stringify({
      notification: {
        ...body.notification,
        notificationPriority: 'PRIORITY_MAX',
        apnsPriority: '10',
        apnsPushType: 'alert',
        analyticsLabel: 'GENERIC',
        apsSound: 'default',
        apsMutableContent: 1,
        androidPriority: 'high',
      },
      app: body.app,
      id: body.id,
      scheduledTime: body.scheduledTime,
      createdBy: 'USER',
      createdAt: moment().format(),
      status: 'SCHEDULED',
    }),
    headers: getHeaders(auth, {
      'Content-Type': 'application/json',
    }),
  });

  const response = await res.json();
  if (response.error || !response.id) {
    throw new Error(response?.error || '');
  }

  return response;
};

export const scheduleNewsNotification = async ({
  abbrv,
  body,
  auth,
}: {
  auth?: string;
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image: string;
      articleUrl: string;
      target: PushNotificationTarget;
    };
    id?: string;
    scheduledTime: string;
  };
}): Promise<{ id: string; message: string }> => {
  const response = await scheduleNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'NEWS',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
    },
    auth,
  });

  return response;
};

export const scheduleStoryNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image?: string;
      storiesId: string;
      target: PushNotificationTarget;
    };
    id?: string;
    scheduledTime: string;
  };
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const response = await scheduleNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'STORIES',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
    },
    auth,
  });

  return response;
};

export const schedulePollNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image?: string;
      pollId: string;
      target: PushNotificationTarget;
    };
    id?: string;
    scheduledTime: string;
  };
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const response = await scheduleNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'POLL',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
    },
    auth,
  });

  return response;
};

export const scheduleArticleNotification = async ({
  abbrv,
  body,
  auth,
}: {
  auth?: string;
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image: string;
      articleUrl: string;
      target: PushNotificationTarget;
      articlesId: string;
    };
    id?: string;
    scheduledTime: string;
  };
}): Promise<{ id: string; message: string }> => {
  const response = await scheduleNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'ARTICLES',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
    },
    auth,
  });

  return response;
};

export const scheduleFotbollPlayNotification = async ({
  abbrv,
  body,
  auth,
}: {
  auth?: string;
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image?: string;
      videoUrl: string;
      target: PushNotificationTarget;
    };
    id?: string;
    scheduledTime: string;
  };
}): Promise<{ id: string; message: string }> => {
  const response = await scheduleNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'FOTBOLLPLAY',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
    },
    auth,
  });

  return response;
};

export const scheduleMatchNotification = async ({
  abbrv,
  body,
  auth,
}: {
  auth?: string;
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      matchId: string;
      leagueName: string;
      season: string;
      target: PushNotificationTarget;
    };
    id?: string;
    scheduledTime: string;
  };
}): Promise<{ id: string; message: string }> => {
  const response = await scheduleNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'MATCH',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
    },
    auth,
  });

  return response;
};

export const updateScheduledNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: ScheduledNotificationPayload;
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const res = await fetch(getScheduleApiUrl(`/${abbrv}`), {
    method: 'PUT',
    body: JSON.stringify({
      notification: {
        ...body.notification,
        notificationPriority: 'PRIORITY_MAX',
        apnsPriority: '10',
        apnsPushType: 'alert',
        analyticsLabel: 'GENERIC',
        apsSound: 'default',
        apsMutableContent: 1,
        androidPriority: 'high',
      }, // satisfies PushdNotificationDataWithOptions, // TODO fix
      app: body.app,
      id: body.id,
      scheduledTime: body.scheduledTime,
      status: 'SCHEDULED',
      createdBy: 'USER',
      createdAt: body.createdAt,
    }),
    headers: getHeaders(auth, {
      'Content-Type': 'application/json',
    }),
  });

  const response = await res.json();
  if (response.error) {
    throw new Error(response?.error || '');
  }

  return response;
};

export const updateScheduledNewsNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image: string;
      articleUrl: string;
      target: PushNotificationTarget;
    };
    id: string;
    scheduledTime: string;
    createdAt: string;
  };
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const response = await updateScheduledNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'NEWS',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
      createdAt: body.createdAt,
    },
    auth,
  });

  return response;
};

export const updateScheduledStoryNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image?: string;
      storiesId: string;
      target: PushNotificationTarget;
    };
    id: string;
    scheduledTime: string;
    createdAt: string;
  };
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const response = await updateScheduledNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'STORIES',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
      createdAt: body.createdAt,
    },
    auth,
  });

  return response;
};

export const updateScheduledPollNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image?: string;
      pollId: string;
      target: PushNotificationTarget;
    };
    id: string;
    scheduledTime: string;
    createdAt: string;
  };
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const response = await updateScheduledNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'POLL',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
      createdAt: body.createdAt,
    },
    auth,
  });

  return response;
};

export const updateScheduledArticleNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image?: string;
      articleUrl?: string;
      target: PushNotificationTarget;
      articlesId: string;
    };
    id: string;
    scheduledTime: string;
    createdAt: string;
  };
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const response = await updateScheduledNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'ARTICLES',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
      createdAt: body.createdAt,
    },
    auth,
  });

  return response;
};

export const updateScheduledFotbollPlayNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      image?: string;
      videoUrl: string;
      target: PushNotificationTarget;
    };
    id: string;
    scheduledTime: string;
    createdAt: string;
  };
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const response = await updateScheduledNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'FOTBOLLPLAY',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
      createdAt: body.createdAt,
    },
    auth,
  });

  return response;
};

export const updateScheduledMatchNotification = async ({
  abbrv,
  body,
  auth,
}: {
  abbrv: string;
  body: {
    notification: {
      title: string;
      description: string;
      matchId: string;
      leagueName: string;
      season: string;
      target: PushNotificationTarget;
    };
    id: string;
    scheduledTime: string;
    createdAt: string;
  };
  auth?: string;
}): Promise<{ id: string; message: string }> => {
  const response = await updateScheduledNotification({
    abbrv,
    body: {
      notification: {
        ...body.notification,
        type: 'MATCH',
      },
      app: abbrv,
      id: body.id,
      scheduledTime: body.scheduledTime,
      createdAt: body.createdAt,
    },
    auth,
  });

  return response;
};

export const deleteNotification = async (
  id: string,
  auth: string
): Promise<{ id: string; message: string }> => {
  const res = await fetch(getScheduleApiUrl(`/${id}`), {
    method: 'DELETE',
    headers: getHeaders(auth, {
      'Content-Type': 'application/json',
    }),
  });

  const response = await res.json();

  return response;
};

export const getScheduledNotification = async (
  abbrv: string,
  id: string,
  auth: string
): Promise<SchedulePushdNotificationBody> => {
  const res = await fetch(getScheduleApiUrl(`/${abbrv}/${id}`), {
    method: 'GET',
    headers: getHeaders(auth),
  });

  const response = await res.json();

  // if (response.error) {
  //   throw new Error(response?.error || '');
  // }

  return response;
};

export const getScheduledNotifications = async (
  abbrv: string,
  auth: string
): Promise<SchedulePushdNotificationBody[]> => {
  const res = await fetch(getScheduleApiUrl(`/${abbrv}`), {
    method: 'GET',
    headers: getHeaders(auth),
  });

  const response = await res.json();

  // if (response.error) {
  //   throw new Error(response?.error || '');
  // }

  return Object.values(response);
};
